// A selection moved as a group (issue 830): who comes along, where each one
// stands while the leader is dragged, and the routes the one event carries.
// Pure, so it is graded without Pixi; the canvas calls it.
//
// The rule (design/11 Q3, the reading on the issue): a group move carries the
// selected things of the SAME KIND as the one grabbed — tokens with a token,
// props with a prop — and never a locked one (design/11 §7). A box over a room
// still selects everything for a reveal; a drag moves the creatures and leaves
// the furniture standing.
import { OBJECT_LIMITS, type Point, type Size } from '@dragons-table/vtt-shared';
import { capRoute } from './route';

export interface GroupThing { id: string; kind: string; x: number; y: number; size: Size; locked: boolean; rev: number }
export interface Follower { id: string; start: Point; size: Size; rev: number }

/**
 * The count a move is made against (the tie, design/21 §4.2). A player's projection carries it only on their own
 * things (issue 856), and only the DM or an owner ever sends a move, so it is present wherever this is read; 0 is
 * the reading of an older snapshot, never a guess about somebody else's thing.
 */
export const moveRev = (o: { rev?: number }): number => o.rev ?? 0;

/** The things that come along with `leaderId`, and how many locked ones of its kind stay behind. */
export function groupOf(objects: Record<string, GroupThing>, selected: Iterable<string>, leaderId: string): { followers: Follower[]; lockedStayed: number } {
  const leader = objects[leaderId];
  const followers: Follower[] = [];
  let lockedStayed = 0;
  if (!leader) return { followers, lockedStayed };
  for (const id of selected) {
    if (id === leaderId) continue;
    const o = objects[id];
    if (!o || o.kind !== leader.kind) continue;
    if (o.locked) { lockedStayed += 1; continue; }
    followers.push({ id, start: { x: o.x, y: o.y }, size: { ...o.size }, rev: moveRev(o) });
  }
  return { followers, lockedStayed };
}

/** Where a follower stands when the leader has gone from `leaderStart` to `leaderAt`: the same offset, on its own square. */
export function followerAt(f: Follower, leaderStart: Point, leaderAt: Point, snap: ((p: Point, size: Size) => Point) | null): Point {
  const p = { x: f.start.x + (leaderAt.x - leaderStart.x), y: f.start.y + (leaderAt.y - leaderStart.y) };
  return snap ? snap(p, f.size) : p;
}

/**
 * The routes of one group move: the leader's own, and each follower's the leader's moved by the follower's offset,
 * its last point on the follower's own square. Placing the table (issue 811), every route is its last point alone.
 */
export function groupMoves(
  leader: { id: string; rev: number }, route: Point[], followers: Follower[],
  snap: ((p: Point, size: Size) => Point) | null, placing: boolean,
): Array<{ id: string; route: Point[]; rev: number }> {
  const start = route[0]!;
  const end = route[route.length - 1]!;
  // Every route thinned alike, so the one event stays under the wire's limit for a group however many come along.
  const per = Math.max(2, Math.min(OBJECT_LIMITS.route, Math.floor(OBJECT_LIMITS.groupPoints / (followers.length + 1))));
  route = capRoute(route, per);
  const moves = [{ id: leader.id, route: placing ? [end] : route, rev: leader.rev }];
  for (const f of followers) {
    const shifted = route.map((p) => ({ x: f.start.x + (p.x - start.x), y: f.start.y + (p.y - start.y) }));
    const last = followerAt(f, start, end, snap);
    shifted[shifted.length - 1] = last;
    moves.push({ id: f.id, route: placing ? [last] : shifted, rev: f.rev });
  }
  return moves;
}

/** The things the arrow keys step together: the selected tokens (or, with none, the selected props), never a locked one. */
export function steppable(objects: Record<string, GroupThing>, selected: Iterable<string>): GroupThing[] {
  const picked = [...selected].map((id) => objects[id]).filter((o): o is GroupThing => Boolean(o));
  const kind = picked.some((o) => o.kind === 'token') ? 'token' : 'prop';
  return picked.filter((o) => o.kind === kind && !o.locked);
}
